/**
 * Shared utilities for contest ID/index conversion
 * Used across Newsfeed page and ComposePost component
 */

/**
 * Get the initial contest ID from user's previous contest selection
 * @param {object} user - The user object with PrevContest field
 * @param {array} userRoles - Array of user roles with ContestID fields
 * @returns {number} - The contest ID (0 for "Glow Game" default)
 */
export const getInitialContestID = (user, userRoles) => {
	const prevContestID = user?.PrevContest;
	if (!prevContestID || prevContestID === 0) {
		return 0; // Default to "Glow Game"
	}

	// Verify the contest exists in user roles
	const prevContestRole = userRoles?.find(
		(role) => role.ContestID === prevContestID
	);

	return prevContestRole ? prevContestID : 0;
};

/**
 * Convert contest ID to index for Select component
 * @param {number} contestID - The contest ID from the database
 * @param {array} userRoles - Array of user roles with ContestID and ContestName
 * @param {array} contests - Array of contest names (first item is "Glow Game")
 * @returns {number} - The index in the contests array
 */
export const getContestIndexFromID = (contestID, userRoles, contests) => {
	if (contestID === 0) {
		return 0; // "Glow Game"
	}

	// Find the contest name from user roles
	const contestRole = userRoles?.find((role) => role.ContestID === contestID);

	if (contestRole) {
		// Find the index of this contest in the contests array
		const index = contests.findIndex(
			(c) => c.toLowerCase() === contestRole.ContestName.toLowerCase()
		);
		return index > 0 ? index : 0;
	}

	return 0;
};

/**
 * Convert contest index to ID for API calls
 * @param {number} index - The index in the contests array
 * @param {array} userRoles - Array of user roles with ContestID and ContestName
 * @param {array} contests - Array of contest names (first item is "Glow Game")
 * @returns {number} - The contest ID for API calls
 */
export const getContestIDFromIndex = (index, userRoles, contests) => {
	if (index === 0) {
		return 0; // "Glow Game"
	}

	const contestName = contests[index];
	if (!contestName) {
		return 0;
	}

	// Find matching role by contest name
	const contestRole = userRoles?.find(
		(role) => role.ContestName.toLowerCase() === contestName.toLowerCase()
	);

	return contestRole ? contestRole.ContestID : 0;
};

/**
 * Build contests array from user roles
 * @param {function} getContestNames - Function to get contest names from user store
 * @returns {array} - Array of contest names with "Glow Game" first
 */
export const buildContestsArray = (getContestNames) => {
	const contestNames = getContestNames?.() || [];
	return [
		"Glow Game",
		...contestNames.filter((name) => name.toLowerCase() !== "the glow game"),
	];
};
